import { ChangeDetectionStrategy, Component, Signal, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { HdPlanetInfoService } from '../../chart/bodygraph/hd-planet-info.service';
import { HdPlanetCardComponent } from '../../chart/bodygraph/planet-info/hd-planet-card.component';
import { HdPlanetKey } from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { HdTransitInfoCardComponent } from '../info-card/hd-transit-info-card.component';
import { HdTransitInfoCardData, nowNextToInfoCardData } from '../info-card/hd-transit-info-card.models';
import { segmentsToInfoCardData } from '../info-card/hd-transit-info-card.utils';
import { buildHdTransitGateInfoPanel } from '../transit-gate-info.utils';
import { HdTransitShiftPopupData } from './hd-transit-shift-popup.models';

const POPUP_I18N_PREFIX = 'humanDesign.chart.transits.popup';

/**
 * Gedeelde transit-shift-popup (spec §4): bovenaan de transit-info-card
 * (header, poort-/lijn-rij met keynotes en center-pills), daaronder de links
 * naar de vorige/huidige/volgende poort-details en onderaan de planet
 * cheat-sheet card. Wordt gevoed door het Nu & Straks-panel (F2) en de
 * ephemeris-lanes (F3) via {@link HdTransitShiftPopupService}.
 *
 * De data kan op twee manieren worden aangeleverd (patroon van
 * `HdGateInfoRequestedComponent`):
 *  1. als `[data]`-input bij direct template-gebruik;
 *  2. via het `FLOATING_PANEL_DATA`-token wanneer de popup door de
 *     `FloatingPanelService` gemount wordt — sluiten gaat dan via het kruisje
 *     van de panel-host of Esc (floating panels hebben geen backdrop).
 */
@Component({
  selector: 'hd-transit-shift-popup',
  templateUrl: './hd-transit-shift-popup.component.html',
  styleUrl: './hd-transit-shift-popup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdPlanetCardComponent, HdTransitInfoCardComponent],
})
export class HdTransitShiftPopupComponent {
  readonly data = input<HdTransitShiftPopupData | null>(null);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdTransitShiftPopupData>
    | null;

  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly planetInfo = inject(HdPlanetInfoService);

  private readonly lang = computed(() => {
    // Meelezen zodat de eerste translate ná een async translation-load opnieuw
    // evalueert (zie HdContentLanguageService.translationsLoadedTick).
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly effectiveData = computed<HdTransitShiftPopupData | null>(
    () => this.data() ?? this.panelData?.() ?? null,
  );

  /**
   * Invoer van de transit-info-card: de now-next-context wint; anders wordt
   * prev/next afgeleid uit de meegegeven lanes-segmenten; als laatste
   * resteert een kale kaart met enkel de aangeklikte stand.
   */
  protected readonly cardData = computed<HdTransitInfoCardData | null>(() => {
    const data = this.effectiveData();
    if (data == null || data.planetKey == null) return null;
    const context = data.context ?? null;
    if (context != null) {
      const cardData = nowNextToInfoCardData(context);
      if (cardData != null) {
        return { ...cardData, fromUtc: data.fromUtc, toUtc: data.toUtc, stations: data.stations ?? [] };
      }
    }
    const fromUtc = data.fromUtc ?? null;
    if (fromUtc == null) return null; // kale popup zonder tijden → geen info-card
    const toUtc = data.toUtc ?? fromUtc;
    return segmentsToInfoCardData(
      data.planetKey,
      data.segments ?? [],
      { gate: data.gate, line: data.line, fromUtc, toUtc },
      data.retrograde ?? false,
      data.stations ?? [],
    );
  });

  /** Poort-links onder de kaart — enkel als het een ander poortnummer is. */
  protected readonly prevGateLink = computed<number | null>(() => {
    const cardData = this.cardData();
    if (cardData?.prevGate == null || cardData.prevGate.gate === cardData.current.gate) return null;
    return cardData.prevGate.gate;
  });

  protected readonly nextGateLink = computed<number | null>(() => {
    const cardData = this.cardData();
    if (cardData?.nextGate == null || cardData.nextGate.gate === cardData.current.gate) return null;
    return cardData.nextGate.gate;
  });

  protected readonly moreInfoLabel = computed(() => {
    const data = this.effectiveData();
    return this.t('moreInfoLink', { gate: data?.gate ?? 0 });
  });

  protected readonly prevGateLinkLabel = computed(() => this.t('prevGateLink', { gate: this.prevGateLink() ?? 0 }));
  protected readonly nextGateLinkLabel = computed(() => this.t('nextGateLink', { gate: this.nextGateLink() ?? 0 }));

  /** Planet cheat-sheet card: enkel zinvol wanneer er vertaalde content is. */
  protected readonly planetInfoKey = computed<HdPlanetKey | null>(() => {
    const data = this.effectiveData();
    if (data?.planetKey == null) return null;
    const info = this.planetInfo.planetInfoIn(data.planetKey, this.lang());
    return this.hasPlanetContent(info) ? data.planetKey : null;
  });

  protected t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`${POPUP_I18N_PREFIX}.${key}`, params, this.lang());
  }

  /** Heeft de vertaalde planeet-info meer content dan enkel de (fallback-)naam? */
  private hasPlanetContent(info: { subtitle?: string; bullets?: readonly string[] }): boolean {
    return (info.subtitle ?? '').trim().length > 0 || (info.bullets?.length ?? 0) > 0;
  }

  /**
   * Poort-links ("Meer over poort X", prev/next): opent het bestaande
   * gate-info floating panel in de normale (full) variant met de
   * transiterende planeet als chart-context — gedeeld met de verticale
   * dag-flow via {@link buildHdTransitGateInfoPanel}. Enkel de huidige poort
   * krijgt de planeet als activatie (anders toont de vorige/volgende poort de
   * planeet-invloed van de verkeerde poort.lijn, bv. "54.6" in poort 61).
   */
  protected openGateInfo(gate: number): void {
    const data = this.effectiveData();
    if (data == null) return;
    this.floatingPanels.openOrFocus(
      buildHdTransitGateInfoPanel(gate, {
        planetId: data.planetId,
        planetKey: data.planetKey,
        gate: data.gate,
        line: data.line,
        sourceKey: data.context?.sourceKey,
      }),
    );
  }
}
